import React from 'react';
import {useSize} from '@packages/utilities/styled';

import {CREATABLE_OPTION_VALUE, isOptionGroup} from '../../../hooks';

import Spinner from '../../Spinner';
import View from '../../View';

import {SearchableListItemWrapper} from '../../SearchableList/SearchableListItem/SearchableListItem';
import {
  SearchableListItemGroupView,
  SearchableListBoxProps,
} from '../../SearchableList/SearchableListBox';
import {SearchableListGroupProps} from '../../SearchableList/SearchableListBox/_types';
import {useSearchableListBox} from '../../SearchableList/useSearchableListBox';
import {useSearchableListContext} from '../../SearchableList/useSearchableList';

import {AutocompleteCreatableOption} from '../AutocompleteOption';
import AutocompleteNote from './AutocompleteNote';
import {AutocompleteProps} from '../_types';
import {
  AutocompleteListBoxView,
  AutocompleteListBoxContentView,
} from './AutocompleteListBox.styles';
import {getDividerHeight, getListBoxPadding, getOptionHeight} from './_utils';
import {useAutocompleteNoteText} from './useAutocompleteNoteText';
import {AutocompleteEmptyState} from './AutocompleteEmptyState';
import {AutocompleteGroupLabel} from './AutocompleteGroupLabel';

interface AutocompleteListBoxProps extends SearchableListBoxProps {
  isCreatable?: AutocompleteProps['isCreatable'];
  maxHeight?: AutocompleteProps['maxHeight'];
  note?: React.ReactNode;
  renderEmptyStateLabel?: AutocompleteProps['renderEmptyStateLabel'];
  shouldPreventBodyScroll?: AutocompleteProps['shouldPreventBodyScroll'];
}

const NonVirtualizedAutocompleteListBoxGroup = ({
  density,
  label,
  options,
  renderOption,
  shouldHideLabel,
  verticalItemAlignment,
}: SearchableListGroupProps) => {
  const groupLabelId = React.useId();

  return (
    <SearchableListItemGroupView aria-labelledby={groupLabelId} role="group">
      <AutocompleteGroupLabel
        id={groupLabelId}
        isVisuallyHidden={shouldHideLabel}
      >
        {label}
      </AutocompleteGroupLabel>
      {options.map((option) => (
        <SearchableListItemWrapper
          categoryLabel={label}
          density={density}
          key={option.value}
          option={option}
          renderOption={renderOption}
          verticalItemAlignment={verticalItemAlignment}
        />
      ))}
    </SearchableListItemGroupView>
  );
};

export const NonVirtualizedAutocompleteListBox = React.forwardRef<
  HTMLDivElement,
  AutocompleteListBoxProps
>(
  (
    {
      density = 'compact',
      hasGroupDividers = false,
      isCreatable,
      maxHeight,
      renderEmptyStateLabel,
      verticalItemAlignment = 'top',
      note: noteProp,
      ...props
    },
    ref
  ) => {
    const {floatingStyles, isLoading, refs} = useSearchableListContext();
    const {mergedProps, options, renderOption} = useSearchableListBox({
      ref,
      ...props,
    });

    const autocompleteNote = useAutocompleteNoteText({defaultNote: noteProp});
    const noteRef = React.useRef<HTMLDivElement>(null);
    const [noteHeight, setNoteHeight] = React.useState<number>(0);

    const [isRenderingCreatable, setIsRenderingCreatable] =
      React.useState<boolean>(false);

    const creatablePortalRef = React.useRef<HTMLDivElement>(null);

    const size = useSize();

    const floatingRef = refs.floating.current;

    const calculatedMinHeight = React.useMemo(() => {
      if (!floatingRef) return 0;

      /**
       * If there are no options, we still want to account for the height of one.
       * This is because the EmptyStateWrapper (i.e. the "No results" message) is the same height as an option.
       */
      const optionsLength = options.length ? options.length : 1;

      const optionWrapperMinHeight =
        Math.min(3, optionsLength) * getOptionHeight(size);

      const listBoxPadding = getListBoxPadding(size);

      let maxNoteHeight = 0;
      if (autocompleteNote && noteRef.current) {
        maxNoteHeight = noteHeight;
      }

      let creatableDividerHeight = 0;
      if (isRenderingCreatable && options.length > 1) {
        creatableDividerHeight = getDividerHeight(size);
      }

      const autocompleteMinHeight =
        listBoxPadding +
        optionWrapperMinHeight +
        creatableDividerHeight +
        maxNoteHeight;

      return autocompleteMinHeight;
    }, [
      autocompleteNote,
      floatingRef,
      isRenderingCreatable,
      noteHeight,
      options,
      size,
      noteRef,
    ]);

    return (
      <AutocompleteListBoxView
        display="flex"
        flexDirection="column"
        hasGroupDividers={hasGroupDividers}
        maxHeight={maxHeight}
        minHeight={calculatedMinHeight}
        style={floatingStyles}
        {...mergedProps}
      >
        <AutocompleteListBoxContentView role="presentation">
          {isLoading ? (
            <View
              aria-label="Loading..."
              display="flex"
              justifyContent="center"
              padding="$5 $0"
              role="option"
            >
              <Spinner />
            </View>
          ) : options.length ? (
            <>
              {options.map((option) => {
                if (isOptionGroup(option)) {
                  const {
                    options: groupedOptions,
                    label,
                    shouldHideGroupLabel,
                  } = option;

                  if (!groupedOptions.length) {
                    return null;
                  }
                  return (
                    <NonVirtualizedAutocompleteListBoxGroup
                      density={density}
                      key={label}
                      label={label}
                      options={groupedOptions}
                      renderOption={renderOption}
                      shouldHideLabel={shouldHideGroupLabel}
                      verticalItemAlignment={verticalItemAlignment}
                    />
                  );
                }

                if (option.value === CREATABLE_OPTION_VALUE) {
                  return (
                    <AutocompleteCreatableOption
                      density={density}
                      key={option.value}
                      onMount={() => setIsRenderingCreatable(true)}
                      onUnmount={() => setIsRenderingCreatable(false)}
                      option={option}
                      portalRoot={creatablePortalRef.current}
                      shouldRenderDivider={options.length > 1}
                      verticalItemAlignment={verticalItemAlignment}
                    />
                  );
                }

                return (
                  <span key={option.value}>
                    <SearchableListItemWrapper
                      density={density}
                      option={option}
                      renderOption={renderOption}
                      verticalItemAlignment={verticalItemAlignment}
                    />
                  </span>
                );
              })}
            </>
          ) : (
            <AutocompleteEmptyState
              renderEmptyStateLabel={renderEmptyStateLabel}
            />
          )}
        </AutocompleteListBoxContentView>
        {isCreatable ? <div ref={creatablePortalRef} /> : null}
        {autocompleteNote && options.length > 0 ? (
          <AutocompleteNote ref={noteRef} setNoteHeight={setNoteHeight}>
            {autocompleteNote}
          </AutocompleteNote>
        ) : null}
      </AutocompleteListBoxView>
    );
  }
);
